import { useSyncExternalStore } from 'react';

export type SseStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'listening'
  | 'error';

type SseState = {
  state: SseStatus;
  messages: SseEvent[];
  isLeader: boolean;
};

type BaseSseEvent = {
  id: string;
  receivedAt: string;
};

type NotificationSnapshotEvent = BaseSseEvent & {
  event: 'notifications.snapshot';
  data: {
    event_id: string;
    type: 'notifications.snapshot';
    unread_count: number;
  };
};

type NotificationAction = {
  label: string;
  href: string;
  actionId: string;
  variant: 'primary' | 'secondary' | 'danger' | string;
};

type NotificationInAppCreatedData = {
  event_id: string;
  type: 'notification.in_app.created';
  user_id: string;
  notification_recipient_id: string;
  notification_id: string;
  subject: string;
  severity: 'info' | 'success' | 'warning' | 'error' | string;
  subtitle: string | null;
  body_markdown: string;
  actions: NotificationAction[];
  created_at: string;
  unread_count: number;
};

type NotificationInAppEvent = BaseSseEvent & {
  event: 'notification.in_app.created';
  data: NotificationInAppCreatedData;
};

type PingEvent = BaseSseEvent & { event: 'ping'; data: { ok: true } };

export type SseEvent =
  | NotificationSnapshotEvent
  | NotificationInAppEvent
  | PingEvent;

type GenericSseEvent = BaseSseEvent & { event: string; data: unknown };

const MAX_MESSAGES = 250;

let state: SseState = { state: 'idle', messages: [], isLeader: false };
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function setSseState(newState: SseState['state']) {
  if (state.state === newState) return;

  state = { ...state, state: newState };
  notify();
}

export function setSseLeader(isLeader: boolean) {
  if (state.isLeader === isLeader) return;

  state = { ...state, isLeader };
  notify();
}

export function publishSseMessage(message: GenericSseEvent) {
  state = {
    ...state,
    messages: [message, ...state.messages].slice(0, MAX_MESSAGES) as SseEvent[],
  };
  notify();
}

export function clearSseMessages() {
  if (state.messages.length === 0) return;

  state = { ...state, messages: [] };
  notify();
}

export function subscribeToSse(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSseState() {
  return state;
}

export function useSseState() {
  return useSyncExternalStore(subscribeToSse, getSseState);
}
